import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Building2Icon, QuoteIcon, ShieldCheckIcon } from 'lucide-react';
import { getAttribution, verifiedLabel } from '../../utils/reviews';
import type { Review } from '../../types/content';
interface ReviewCardProps {
  review: Review;
  index: number;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function ReviewCard({
  review,
  index
}: ReviewCardProps) {
  const reduce = useReducedMotion();
  const {
    name,
    title
  } = getAttribution(review);
  const showPhoto = Boolean(review.photoUrl) && !review.anonymous;
  return <motion.figure className="group flex h-full flex-col rounded-lg border border-champagne-100/10 bg-midnight-900 p-6 transition-[border-color,box-shadow] duration-200 ease-out hover:border-champagne-300/30 hover:shadow-[0_20px_40px_-24px_rgba(0,0,0,0.8)] md:p-7" initial={reduce ? false : {
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} whileHover={reduce ? undefined : {
    y: -3
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    duration: 0.3,
    ease: EASE,
    delay: Math.min(index, 3) * 0.05
  }} data-id="element-271" data-mp="9u7ssgu80c99">
      <QuoteIcon className="h-5 w-5 text-champagne-400" aria-hidden="true" data-id="element-272" data-mp="lifvxpgvl15a" />
      <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-champagne-100/85 md:text-base" data-id="element-273" data-mp="7q850ec9w4ei">
        <p data-id="element-274" data-mp="coj9e9jstmpc">{review.quote}</p>
      </blockquote>
      <figcaption className="mt-6 flex items-center gap-3 border-t border-champagne-100/10 pt-5" data-id="element-275" data-mp="10dvjrcqv40t8">
        <span className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-champagne-100/5 ring-1 ring-champagne-300/25" data-id="element-276" data-mp="14exiztks4cv7">
          {showPhoto ? <img src={review.photoUrl} alt={review.photoAlt ?? `Portrait of ${name}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.05]" data-id="element-277" data-mp="qewhqd0lr3f7" /> : review.anonymous ? <span className="flex h-full w-full items-center justify-center" data-id="element-278" data-mp="3g2nf99utkmiu">
              <Building2Icon className="h-5 w-5 text-champagne-300/60" aria-hidden="true" data-id="element-279" data-mp="2w23zzidvhkvs" />
            </span> : <span className="flex h-full w-full items-center justify-center font-serif text-lg text-champagne-300" aria-hidden="true" data-id="element-280" data-mp="2s34vtzzyts5e">
              {name.charAt(0)}
            </span>}
        </span>
        <span className="min-w-0" data-id="element-281" data-mp="2i9ea32v83vte">
          <span className="block truncate text-[15px] font-medium text-champagne-50" data-id="element-282" data-mp="2dce4su676x3c">{name}</span>
          {title && <span className="block truncate text-sm text-champagne-100/60" data-id="element-283" data-mp="28bzvz9wrvj49">{title}</span>}
        </span>
        {review.verified && <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-champagne-300" title={verifiedLabel(review.relationship)} data-id="element-284" data-mp="24dk2cn66rchv">
            <ShieldCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-285" data-mp="2j1mrnwc659m" />
            <span className="hidden sm:inline" data-id="element-286" data-mp="3tpul8jzqvqag">{verifiedLabel(review.relationship)}</span>
            <span className="sr-only sm:hidden" data-id="element-287" data-mp="54sjbfnpf4le">{verifiedLabel(review.relationship)}</span>
          </span>}
      </figcaption>
    </motion.figure>;
}